/**
 * Search – Predictive Search
 * The Color – thecolor.uk
 *
 * Handles:
 *  - Predictive search overlay with debounced input
 *  - Fetches results from Shopify's predictive search API (per-type limits)
 *  - Empty-overlay routes: Popular + Popular searches
 *  - Keyboard navigation through results (ArrowDown/ArrowUp)
 *  - Focus management, focus trapping, and accessibility
 */

(function () {
  'use strict';

  const DEBOUNCE_MS = 300;
  const MIN_CHARS = 3;

  /* ALL toggles (2026-08-20): the header now has a desktop search bar AND a
     mobile icon, both [data-search-toggle] — querySelector bound only the
     first, silently killing whichever rendered second. */
  const searchToggles = Array.from(document.querySelectorAll('[data-search-toggle]'));
  let lastToggle = null;
  const searchOverlay = document.getElementById('search-overlay');
  const searchInput = searchOverlay?.querySelector('[data-search-input]');
  const searchResults = searchOverlay?.querySelector('[data-search-results]');
  const searchClose = searchOverlay?.querySelector('[data-search-close]');
  const searchForm = searchOverlay?.querySelector('form');

  let debounceTimer = null;
  let abortController = null;
  let releaseFocus = null;

  /* -----------------------------------------------------------------------
     1. Toggle Overlay
     ----------------------------------------------------------------------- */

  function openSearch() {
    if (!searchOverlay) return;

    searchOverlay.removeAttribute('hidden');
    searchOverlay.setAttribute('aria-hidden', 'false');
    searchToggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
    document.body.style.overflow = 'hidden';

    // Trap focus inside overlay
    if (window.TheColor && window.TheColor.trapFocus) {
      releaseFocus = window.TheColor.trapFocus(searchOverlay);
    }

    requestAnimationFrame(() => {
      searchInput?.focus();
    });

    if (searchInput && !searchInput.value.trim()) renderDefaultState();
  }

  function closeSearch() {
    if (!searchOverlay) return;

    searchOverlay.setAttribute('hidden', '');
    searchOverlay.setAttribute('aria-hidden', 'true');
    searchToggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    document.body.style.overflow = '';

    // Release focus trap
    if (releaseFocus) {
      releaseFocus();
      releaseFocus = null;
    }

    // Clear results
    if (searchResults) searchResults.innerHTML = '';
    if (searchInput) searchInput.value = '';

    (lastToggle || searchToggles[0])?.focus();
  }

  searchToggles.forEach((toggle) => toggle.addEventListener('click', (e) => {
    lastToggle = toggle;
    e.preventDefault();
    openSearch();
  }));

  searchClose?.addEventListener('click', closeSearch);

  // Close on Escape
  searchOverlay?.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeSearch();
      return;
    }

    // Arrow key navigation through results
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const links = searchResults ? Array.from(searchResults.querySelectorAll('.tc-search-predict__link')) : [];
      if (!links.length) return;

      e.preventDefault();
      const currentEl = document.activeElement;
      const currentIdx = links.indexOf(currentEl);

      if (e.key === 'ArrowDown') {
        if (currentEl === searchInput || currentIdx === -1) {
          links[0].focus();
        } else if (currentIdx < links.length - 1) {
          links[currentIdx + 1].focus();
        }
      } else {
        if (currentIdx <= 0) {
          searchInput?.focus();
        } else {
          links[currentIdx - 1].focus();
        }
      }
    }
  });

  // Close on backdrop click
  searchOverlay?.addEventListener('click', (e) => {
    if (e.target === searchOverlay) closeSearch();
  });

  /* -----------------------------------------------------------------------
     2. Predictive Search
     ----------------------------------------------------------------------- */

  function fetchPredictiveResults(query) {
    if (abortController) abortController.abort();
    abortController = new AbortController();

    const types = [];
    const settings = window.theme || {};
    if (settings.searchShowProducts !== false) types.push('product');
    if (settings.searchShowPages !== false) types.push('page');
    if (settings.searchShowArticles !== false) types.push('article');
    // Free data the API already returns for this store: query completions
    // (rendered on top) and matching collections (their own group).
    types.push('query', 'collection');

    const typesParam = types.length ? `&resources[type]=${types.join(',')}` : '';
    /* limit_scope=each: the limit applies per type. Without it the 6 were
       shared across every type, so articles and pages crowded products down
       to 2 (audit F10). */
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[limit]=6&resources[limit_scope]=each${typesParam}`;

    // Show loading spinner
    if (searchResults) {
      searchResults.innerHTML = '<div class="tc-search-predict__loading"><span class="tc-search-predict__spinner"></span></div>';
    }

    fetch(url, { signal: abortController.signal })
      .then((res) => res.json())
      .then((data) => {
        renderResults(data, query);
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') return;
        // Fetch failed — replace the stuck spinner with a route to the
        // full search page rather than leaving a dead loading state.
        if (searchResults) {
          searchResults.innerHTML = `
            <div class="tc-search-predict__view-all">
              <a href="/search?q=${encodeURIComponent(query)}" class="tc-btn-tertiary">Press Enter to see all results</a>
            </div>`;
        }
      });
  }

  /* Caps per group. Products get the full 6 the API returns; collections,
     pages and articles stay short so products aren't pushed off screen. */
  const MAX_COLLECTIONS = 4;
  const MAX_PAGES = 3;
  const MAX_ARTICLES = 3;

  /* 48px slot, so 100px covers 2x screens. The CDN's width param works on
     every URL shape; the old `_100x` filename rewrite missed the ?v= suffix. */
  function thumbUrl(src) {
    if (!src) return '';
    try {
      const u = new URL(src, window.location.origin);
      u.searchParams.set('width', '100');
      return u.href;
    } catch (e) {
      return '';
    }
  }

  function toPence(value) {
    const n = parseFloat(value);
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  /* Bundles and sets carry the sum of their parts' RRPs as compare-at. That
     isn't an RRP, and whether the set beats our own singles can't be checked
     here, so they show their price only (the product page explains the set). */
  function isBundle(product) {
    if (product.type === 'Duo') return true;
    if ((product.tags || []).some((t) => String(t).toLowerCase() === 'bundle')) return true;
    /* Multi-packs that aren't typed/tagged as sets (e.g. "Freeze and Shine
       Twin Pack (2 × 1 Litre)", typed Hairspray): their compare-at is also a
       sum of parts, so they show their price only too. */
    return /\b(twin|bundle)\b|\d\s*[×x]\s*\d/i.test(product.title || '');
  }

  /* Price line. Honesty rules:
     - several sizes at different prices: "From £x", never struck through
       (pairing the smallest size's price with the largest size's RRP
       overstated the saving);
     - one price: strike only when that price is below its RRP, and say "RRP";
     - bundles/sets: price only;
     - sold out: plain price + badge (no strike). */
  function priceLine(product, strings) {
    const min = toPence(product.price_min || product.price);
    const max = toPence(product.price_max || product.price);
    const compareMin = toPence(product.compare_at_price_min);
    if (!min) return '';

    const isRange = max > min;
    const priceText = isRange ? `From ${formatMoney(min)}` : formatMoney(min);

    if (product.available === false) {
      const soldOut = strings.soldOut || 'Sold out';
      return `<span class="tc-search-predict__price">${escapeHTML(priceText)}</span>
        <span class="tc-search-predict__badge tc-search-predict__badge--soldout">${escapeHTML(soldOut)}</span>`;
    }

    if (!isRange && !isBundle(product) && compareMin > min) {
      return `<span class="tc-search-predict__price tc-search-predict__price--sale">${formatMoney(min)}</span>
        <span class="tc-search-predict__price tc-search-predict__price--compare">RRP <s>${formatMoney(compareMin)}</s></span>`;
    }

    return `<span class="tc-search-predict__price">${escapeHTML(priceText)}</span>`;
  }

  function linkGroup(title, items) {
    let html = `<div class="tc-search-predict__group">
        <h3 class="tc-search-predict__group-title">${escapeHTML(title)}</h3>
        <ul class="tc-search-predict__list">`;
    items.forEach((item) => {
      html += `
          <li class="tc-search-predict__item">
            <a href="${escapeAttr(item.url)}" class="tc-search-predict__link">
              <span class="tc-search-predict__name">${escapeHTML(item.title)}</span>
            </a>
          </li>`;
    });
    return html + `</ul></div>`;
  }

  function renderResults(data, query) {
    if (!searchResults) return;

    const resources = data?.resources?.results || {};
    const products = resources.products || [];
    const pages = (resources.pages || []).slice(0, MAX_PAGES);
    const articles = (resources.articles || []).slice(0, MAX_ARTICLES);
    const queries = resources.queries || [];
    const collections = (resources.collections || []).slice(0, MAX_COLLECTIONS);
    const strings = (window.theme && window.theme.strings) || {};

    if (!products.length && !pages.length && !articles.length && !collections.length) {
      // The locale string arrives HTML-escaped from Liquid; only the query needs escaping.
      const noResultsMsg = strings.searchNoResults
        ? strings.searchNoResults.replace('__TERMS__', escapeHTML(query))
        : `No results found for &ldquo;${escapeHTML(query)}&rdquo;`;
      searchResults.innerHTML = `
        <div class="tc-search-predict__empty">
          <p>${noResultsMsg}</p>
        </div>
      `;
      // Not a dead end: offer the popular routes underneath.
      renderDefaultState(true);
      return;
    }

    let html = '';

    // Query completions on top — tapping one re-runs the search with that term
    if (queries.length) {
      html += `<div class="tc-search-predict__completions">`;
      queries.slice(0, 4).forEach((q) => {
        html += `<button type="button" class="tc-search-predict__completion" data-search-completion="${escapeAttr(q.text)}">${escapeHTML(q.text)}</button>`;
      });
      html += `</div>`;
    }

    // Matching collections next: a curated page is the best answer to a
    // category-style query ("shampoo", "tea tree", "gift sets").
    if (collections.length) {
      html += linkGroup('Collections', collections);
    }

    if (products.length) {
      const groupTitle = strings.searchProducts || 'Products';
      html += `<div class="tc-search-predict__group">
        <h3 class="tc-search-predict__group-title">${escapeHTML(groupTitle)}</h3>
        <ul class="tc-search-predict__list">`;

      products.forEach((product) => {
        const src = product.image || (product.featured_image && product.featured_image.url) || '';
        const imgUrl = thumbUrl(src);
        // Decorative: the product name sits right beside it in the same link.
        const img = imgUrl
          ? `<img src="${escapeAttr(imgUrl)}" alt="" width="48" height="48" loading="lazy">`
          : '';

        html += `
          <li class="tc-search-predict__item">
            <a href="${escapeAttr(product.url)}" class="tc-search-predict__link">
              <span class="tc-search-predict__img">${img}</span>
              <span class="tc-search-predict__info">
                <span class="tc-search-predict__name">${escapeHTML(product.title)}</span>
                <span class="tc-search-predict__price-wrap">${priceLine(product, strings)}</span>
              </span>
            </a>
          </li>`;
      });

      html += `</ul></div>`;
    }

    if (pages.length) {
      html += linkGroup(strings.searchPages || 'Pages', pages);
    }

    if (articles.length) {
      html += linkGroup(strings.searchArticles || 'Articles', articles);
    }

    // View all link
    html += `
      <div class="tc-search-predict__view-all">
        <a href="/search?q=${encodeURIComponent(query)}" class="tc-btn-tertiary">View all results</a>
      </div>`;

    searchResults.innerHTML = html;
  }

  /* -----------------------------------------------------------------------
     3. Input Handler
     ----------------------------------------------------------------------- */

  searchInput?.addEventListener('input', () => {
    const query = searchInput.value.trim();

    clearTimeout(debounceTimer);

    if (query.length < MIN_CHARS) {
      // Drop any in-flight request so a late response can't overwrite the default panel
      if (abortController) abortController.abort();
      renderDefaultState();
      return;
    }

    debounceTimer = setTimeout(() => {
      fetchPredictiveResults(query);
    }, DEBOUNCE_MS);
  });

  // Tapping a completion re-runs the search with the suggested term
  searchResults?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-search-completion]');
    if (!btn || !searchInput) return;
    searchInput.value = btn.getAttribute('data-search-completion');
    searchInput.focus();
    fetchPredictiveResults(searchInput.value);
  });

  /* Empty-overlay default (nav IA S1): two rows of routes instead of a blank
     panel. Handles are the IA's menu targets; labels match the menu. */
  const POPULAR_LINKS = [
    ['Best Sellers', '/collections/top-selling-products-purchased-by-our-regulars'],
    ['Bundles & Sets', '/collections/duos'],
    ['Gift Sets', '/collections/gift-sets-duos'],
    ['Freeze & Shine', '/products/pm-freeze-and-shine-super-spray'],
    ['1 Litre', '/collections/paul-mitchell-1-litre'],
    ['Travel', '/collections/travel-size'],
    ['Tea Tree', '/collections/tea-tree'],
  ];

  const POPULAR_SEARCHES = [
    'freeze and shine 1000ml',
    'tea tree shampoo',
    'super skinny serum',
    'the detangler',
    'purple shampoo',
  ];

  function chipRow(title, links) {
    const chips = links.map(([label, href]) =>
      `<a class="tc-search-predict__chip" href="${escapeAttr(href)}">${escapeHTML(label)}</a>`
    ).join('');
    return `
      <div class="tc-search-predict__default">
        <h3 class="tc-search-predict__group-title">${escapeHTML(title)}</h3>
        <div class="tc-search-predict__chips">${chips}</div>
      </div>`;
  }

  function renderDefaultState(append) {
    if (!searchResults) return;
    const html = chipRow('Popular', POPULAR_LINKS) +
      chipRow('Popular searches', POPULAR_SEARCHES.map((term) => [term, `/search?q=${encodeURIComponent(term)}`]));
    if (append) searchResults.insertAdjacentHTML('beforeend', html);
    else searchResults.innerHTML = html;
  }

  /* -----------------------------------------------------------------------
     4. Utilities
     ----------------------------------------------------------------------- */

  function escapeHTML(str) {
    return window.TheColor.utils.escapeHtml(str);
  }

  function escapeAttr(str) {
    return window.TheColor.utils.escapeAttr(str);
  }

  function formatMoney(cents) {
    return window.TheColor.utils.formatMoney(cents);
  }
})();
